/* Fasting + period maths — pure, unit-tested (tests/unit/fasting.test.ts).

   Make-up (qaḍāʾ) owed = every Ramadan day not fasted, whether logged as
   missed or excused automatically by a period (women make up Ramadan fasts
   missed during ḥayḍ/nifās, but not the prayers). Make-up fasts reduce the
   oldest year's debt first. */
import { addDays, hijriOn } from '@shared/prayer';
import { fastingInfo } from '@shared/fasting';
import type { FastDay, FastReason, PeriodEntry } from '@/lib/types';

type Coll<T> = Record<string, T>;
const live = <T extends { deleted?: boolean }>(c: Coll<T>) => Object.values(c).filter((x) => !x.deleted);

/** The period covering `dayKey`, if any. An ongoing period covers up to today. */
export function periodOn(periods: Coll<PeriodEntry>, dayKey: string, today: string): PeriodEntry | null {
  for (const p of live(periods)) {
    const end = p.end ?? today;
    if (dayKey >= p.start && dayKey <= end) return p;
  }
  return null;
}

export const ongoingPeriod = (periods: Coll<PeriodEntry>) => live(periods).find((p) => !p.end) ?? null;

export const periodLength = (p: PeriodEntry, today: string) => {
  const end = p.end ?? today;
  return Math.round((Date.parse(`${end}T12:00:00Z`) - Date.parse(`${p.start}T12:00:00Z`)) / 864e5) + 1;
};

export interface EffectiveFast { status: FastDay['status']; kind: FastDay['kind']; reason?: FastReason; derived?: boolean }

/** What happened on a day: the user's log, or — in Ramadan during a period — an automatic excused miss. */
export function effectiveFast(dayKey: string, fasts: Coll<FastDay>, periods: Coll<PeriodEntry>, today: string, hijriOffset = 0): EffectiveFast | null {
  const logged = fasts[dayKey];
  if (logged && !logged.deleted) return logged;
  if (dayKey > today) return null;
  if (fastingInfo(dayKey, hijriOffset).ramadan && periodOn(periods, dayKey, today)) {
    return { status: 'missed', kind: 'ramadan', reason: 'period', derived: true };
  }
  return null;
}

export interface QadaYear { hijriYear: number; missed: number; byReason: Partial<Record<FastReason | 'unspecified', number>>; madeUp: number; remaining: number }
export interface QadaSummary { years: QadaYear[]; totalMissed: number; madeUp: number; remaining: number }

export function qadaSummary(fasts: Coll<FastDay>, periods: Coll<PeriodEntry>, today: string, hijriOffset = 0): QadaSummary {
  const missedDays = new Map<string, FastReason | 'unspecified'>();
  for (const f of live(fasts)) {
    if (f.kind === 'ramadan' && f.status === 'missed' && f.dayKey <= today) missedDays.set(f.dayKey, f.reason ?? 'unspecified');
  }
  // Days inside a period during Ramadan that the user didn't log otherwise.
  for (const p of live(periods)) {
    for (let d = p.start; d <= (p.end ?? today) && d <= today; d = addDays(d, 1)) {
      if (missedDays.has(d)) continue;
      const e = effectiveFast(d, fasts, periods, today, hijriOffset);
      if (e?.derived) missedDays.set(d, 'period');
    }
  }
  const byYear = new Map<number, QadaYear>();
  for (const [d, reason] of missedDays) {
    const y = hijriOn(d, hijriOffset)?.y ?? 0;
    const row = byYear.get(y) ?? { hijriYear: y, missed: 0, byReason: {}, madeUp: 0, remaining: 0 };
    row.missed++;
    row.byReason[reason] = (row.byReason[reason] ?? 0) + 1;
    byYear.set(y, row);
  }
  const years = [...byYear.values()].sort((a, b) => a.hijriYear - b.hijriYear);
  let madeUp = live(fasts).filter((f) => f.kind === 'qada' && f.status === 'fasted' && f.dayKey <= today).length;
  const totalMadeUp = madeUp;
  for (const y of years) {
    const take = Math.min(y.missed, madeUp);
    y.madeUp = take;
    y.remaining = y.missed - take;
    madeUp -= take;
  }
  const totalMissed = years.reduce((a, y) => a + y.missed, 0);
  return { years, totalMissed, madeUp: totalMadeUp, remaining: Math.max(0, totalMissed - totalMadeUp) };
}

/** Voluntary-fast stats for a Hijri year: total, Six of Shawwāl progress. */
export function hijriYearStats(fasts: Coll<FastDay>, hijriYear: number, hijriOffset = 0) {
  let voluntary = 0, shawwal = 0, white = 0, monThu = 0;
  for (const f of live(fasts)) {
    if (f.status !== 'fasted') continue;
    const info = fastingInfo(f.dayKey, hijriOffset);
    if (info.hijri?.y !== hijriYear || f.kind === 'ramadan') continue;
    if (f.kind === 'voluntary') voluntary++;
    if (info.hijri.m === 10) shawwal++;
    if (info.rules.some((r) => r.id === 'white')) white++;
    if (info.rules.some((r) => r.id === 'monThu')) monThu++;
  }
  return { voluntary, shawwal: Math.min(6, shawwal), white, monThu };
}

/** White days fasted in the Hijri month containing `dayKey` (0–3). */
export function whiteDaysThisMonth(fasts: Coll<FastDay>, dayKey: string, hijriOffset = 0) {
  const h = hijriOn(dayKey, hijriOffset);
  if (!h) return 0;
  return live(fasts).filter((f) => {
    if (f.status !== 'fasted') return false;
    const x = hijriOn(f.dayKey, hijriOffset);
    return x && x.y === h.y && x.m === h.m && x.d >= 13 && x.d <= 15;
  }).length;
}
